/**
 * API Error Logger
 * 
 * Axios interceptor that automatically logs API errors to the centralized logging system
 */

import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { logger, ErrorCategory } from './logger';

/**
 * Setup API error logging interceptors
 */
export const setupApiErrorLogging = (): void => {
  // Request interceptor - log outgoing requests in development
  axios.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      logger.debug('API Request:', {
        method: config.method?.toUpperCase(),
        url: config.url,
        data: config.data
      });
      return config;
    },
    (error: AxiosError) => {
      logger.error('API Request Error', {
        message: error.message,
        category: ErrorCategory.NETWORK
      });
      return Promise.reject(error);
    }
  );

  // Response interceptor - log errors
  axios.interceptors.response.use(
    (response: AxiosResponse) => {
      logger.debug('API Response:', {
        status: response.status,
        url: response.config.url,
        data: response.data
      });
      return response;
    },
    (error: AxiosError) => {
      const statusCode = error.response?.status;
      const url = error.config?.url || 'unknown';
      const method = error.config?.method?.toUpperCase() || 'unknown';

      // Determine error category
      let category = ErrorCategory.API;
      if (!error.response) {
        category = ErrorCategory.NETWORK;
      } else if (statusCode === 401 || statusCode === 403) {
        category = ErrorCategory.AUTH;
      }

      // Log the error
      logger.error(error.message || 'API Error', {
        statusCode,
        url,
        method,
        category,
        path: window.location.pathname,
        responseData: error.response?.data,
        requestData: error.config?.data
      });

      return Promise.reject(error);
    }
  );
};

/**
 * Manual API error logger for custom error handling
 */
export const logApiError = (
  error: AxiosError | Error,
  context?: {
    action?: string;
    userId?: string;
    additionalInfo?: Record<string, any>;
  }
): void => {
  const isAxiosError = axios.isAxiosError(error);
  
  const statusCode = isAxiosError ? error.response?.status : undefined;
  const url = isAxiosError ? error.config?.url : undefined;
  const method = isAxiosError ? error.config?.method?.toUpperCase() : undefined;

  logger.error(error.message || 'API Error', {
    statusCode,
    url,
    method,
    category: !isAxiosError || !error.response ? ErrorCategory.NETWORK : 
              (statusCode === 401 || statusCode === 403) ? ErrorCategory.AUTH : 
              ErrorCategory.API,
    path: window.location.pathname,
    action: context?.action,
    userId: context?.userId,
    ...context?.additionalInfo
  });
};

export default { setupApiErrorLogging, logApiError };
